import React, { useEffect, useState } from "react";
import { Trash2, Plus, Pencil } from "lucide-react";
import { INPUT, BTN_PRIMARY, BTN_OUTLINE, Field } from "components/admin/AdminUI";
import api from "lib/api";

export default function Buckets() {
  const [buckets, setBuckets] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // {id?, name, daily_limit}

  const load = () =>
    api.get("/admin/buckets/")
      .then(({ data }) => setBuckets(data.results || data || []))
      .catch(() => setError("Falha ao carregar buckets."));

  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setError("");
    try {
      const payload = { name: editing.name, daily_limit: Number(editing.daily_limit) || 0 };
      if (editing.id) await api.put(`/admin/buckets/${editing.id}/`, payload);
      else await api.post("/admin/buckets/", payload);
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === "object" ? Object.values(d).flat().join(" ") : "Erro ao salvar.");
    }
  }

  async function remove(b) {
    if (!window.confirm(`Excluir o bucket "${b.name}"? Usuários vinculados voltam ao limite padrão.`)) return;
    try { await api.delete(`/admin/buckets/${b.id}/`); load(); }
    catch { setError("Erro ao excluir."); }
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Buckets de rate-limit</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Limite diário de requisições de API por usuário. Padrão (sem bucket): 1000/dia. Admins não têm limite.
          </p>
        </div>
        {!editing && (
          <button className={BTN_PRIMARY} onClick={() => setEditing({ name: "", daily_limit: 1000 })}>
            <Plus className="mr-1 inline h-4 w-4" /> Novo bucket
          </button>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {editing && (
        <form onSubmit={save} className="rounded-lg border border-border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome">
              <input className={INPUT} value={editing.name} autoFocus
                onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
            </Field>
            <Field label="Limite diário (requests/dia)">
              <input className={INPUT} type="number" min="0" value={editing.daily_limit}
                onChange={(e) => setEditing({ ...editing, daily_limit: e.target.value })} required />
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button type="button" className={BTN_OUTLINE} onClick={() => setEditing(null)}>Cancelar</button>
            <button type="submit" className={BTN_PRIMARY}>{editing.id ? "Salvar" : "Criar"}</button>
          </div>
        </form>
      )}

      <div className="rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">Limite diário</th>
              <th className="px-4 py-2 font-medium">Usuários</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {buckets.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">Nenhum bucket.</td></tr>
            )}
            {buckets.map((b) => (
              <tr key={b.id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-2">{b.name}</td>
                <td className="px-4 py-2 font-mono">{b.daily_limit.toLocaleString("pt-BR")}/dia</td>
                <td className="px-4 py-2 text-muted-foreground">{b.user_count ?? 0}</td>
                <td className="px-4 py-2 text-right">
                  <button className="mr-3 text-muted-foreground hover:text-foreground"
                    onClick={() => setEditing({ id: b.id, name: b.name, daily_limit: b.daily_limit })}>
                    <Pencil className="inline h-4 w-4" />
                  </button>
                  <button className="text-red-500 hover:text-red-400" onClick={() => remove(b)}>
                    <Trash2 className="inline h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
